import React, { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import SettingsService from '../../../services/SettingsService';
import MixpanelService from '../../../services/MixpanelService';
import {
  EditInfoButton,
  FieldDealerName,
  FieldDescription,
  FieldDisclaimer,
  FieldGroup,
  ModalBoxContentWrapper,
  ModalHeader,
  ModalOuterBox,
  ModalOuterBoxContainer,
  StyledSubTitle
} from '../Styles';
import RetailToolsEditModal from '../RetailToolsEditModal';
import { isPhoneNumberValid } from '../../../_shared/utils/phoneNumber';
import TRToast from '../../../_shared/helpers/TRToast';
import Alert from '../../../_shared/components/Alert';
import mixpanelConfig from '../../../_shared/constants/mixpanelConfig';
import InputFieldFormatting from '../../../_shared/helpers/InputFieldFormatting';
import RetailToolsRetailVdpModals from './RetailToolsRetailVdpModals';

const RetailToolsRetailVdp = ({
  testId,
  isOmp,
  companyName,
  updateDealerData,
  customizationData,
  dealerData
}) => {
  const [showModal, setShowModal] = useState(false);
  const [phoneInputError, setPhoneInputError] = useState(false);
  const [emailInputError, setEmailInputError] = useState(false);
  const [dealerMetadata, setDealerMetadata] = useState(dealerData);
  const [currentCustomizationData, setCurrentCustomizationData] = useState(
    dealerData
  );
  const settingsService = SettingsService.get();
  const mixpanelService = MixpanelService.get();

  function toggleShowModal() {
    setShowModal(!showModal);
  }

  const handleRetailVdpSave = useCallback(async () => {
    const retailVdpProperties = [
      'salespersonName',
      'salespersonEmail',
      'salespersonPhone',
      'dealershipLink'
    ];

    const requestBody = dealerMetadata;

    const modifiedFields = {
      salespersonName: false,
      salespersonEmail: false,
      salespersonPhone: false,
      dealershipLink: false
    };

    retailVdpProperties.forEach(property => {
      if (
        requestBody?.retailVdp?.[property] !==
        currentCustomizationData?.retailVdp?.[property]
      ) {
        modifiedFields[property] = true;
      }
    });

    const updateResponse = await settingsService.updateDealerRetailInfo(
      requestBody
    );

    if (updateResponse?.isError) {
      setDealerMetadata(dealerData);
      TRToast(
        <Alert
          isOmp={isOmp}
          variant="error"
          messages={[updateResponse?.errorMessage]}
        />
      );
    } else {
      setCurrentCustomizationData(dealerMetadata);
      updateDealerData({ ...dealerMetadata });
      mixpanelService.track(
        mixpanelConfig.names.retailSettingsFieldsUpdated,
        modifiedFields
      );
      TRToast(
        <Alert
          isOmp={isOmp}
          variant="success"
          messages={[
            <FormattedMessage id="retail.tools.setting.confirmation" />
          ]}
        />
      );
    }
  }, [
    currentCustomizationData,
    dealerData,
    dealerMetadata,
    isOmp,
    mixpanelService,
    settingsService,
    updateDealerData
  ]);

  const handleInputFieldChange = e => {
    const { value, id } = e.currentTarget;
    setDealerMetadata(updatedDealerMetadata => ({
      ...updatedDealerMetadata,
      retailVdp: {
        ...updatedDealerMetadata?.retailVdp,
        [id]: value
      }
    }));
    if (id === 'salespersonPhone') {
      const strValue = value.replace(/[^\w\s]/gi, '');

      if (
        !value ||
        value.length === 0 ||
        (strValue && strValue.length !== 10)
      ) {
        return;
      }

      e.currentTarget.value = InputFieldFormatting.formatPhoneNumberInputField(
        strValue
      );

      setDealerMetadata({
        ...dealerMetadata,
        retailVdp: {
          ...dealerMetadata?.retailVdp,
          [id]: e.currentTarget.value
        }
      });
    }
  };
  const handleBlur = e => {
    const { value, id } = e.target;
    if (id === 'salespersonEmail') {
      if (value && !InputFieldFormatting.validEmailFormat(value))
        setEmailInputError(true);
      else setEmailInputError(false);
    }
    if (id === 'salespersonPhone') {
      const isValid = isPhoneNumberValid(value);
      if (value && !isValid) setPhoneInputError(true);
      else setPhoneInputError(false);
    }
  };
  return (
    <React.Fragment key="retailVdp">
      <ModalOuterBox isOmp={isOmp}>
        <ModalBoxContentWrapper>
          <ModalOuterBoxContainer>
            <StyledSubTitle>
              <FormattedMessage id="retail.tools.setting.retailVdp.heading" />
              <FieldDealerName>{companyName}</FieldDealerName>
            </StyledSubTitle>

            <FieldDescription>
              <FormattedMessage id="retail.tools.setting.retailVdp.description" />
            </FieldDescription>
            {Object.entries(customizationData).map(([inputKey, subItem]) => {
              return (
                <React.Fragment key={inputKey}>
                  <FieldGroup>{subItem.displayName}</FieldGroup>
                  <FieldDescription
                    variant={
                      dealerMetadata?.retailVdp?.[inputKey]
                        ? 'value'
                        : 'placeholder'
                    }
                    data-testid={`${testId}-${inputKey}`}
                    id={`${testId}-${inputKey}`}
                  >
                    {dealerMetadata?.retailVdp?.[inputKey]
                      ? dealerMetadata.retailVdp[inputKey]
                      : subItem?.placeholder}
                  </FieldDescription>
                  {inputKey === 'dealershipWebsite' && (
                    <FieldDisclaimer
                      data-testid={`${testId}-linkToUsedDisclaimer`}
                    >
                      <FormattedMessage id="retail.tools.setting.retailVdp.inventory.disclaimer" />
                    </FieldDisclaimer>
                  )}
                </React.Fragment>
              );
            })}
            <EditInfoButton
              testId={`${testId}-retailVdp`}
              onClick={() => toggleShowModal()}
            >
              <FormattedMessage id="list.filters.edit.details" />
            </EditInfoButton>
          </ModalOuterBoxContainer>
        </ModalBoxContentWrapper>
      </ModalOuterBox>
      <RetailToolsEditModal
        testId={`${testId}-uploadModal-retailVdp`}
        isModalOpen={showModal}
        isOmp={isOmp}
        closeHandler={() => {
          setShowModal(false);
          setDealerMetadata(currentCustomizationData);
        }}
        saveHandler={async () => {
          await handleRetailVdpSave();
          setShowModal(false);
        }}
        disableSaveCondition={phoneInputError || emailInputError}
      >
        <ModalHeader>
          <span>
            <FormattedMessage id="retail.tools.setting.retailVdp.heading.for" />
            {` ${companyName}`}
          </span>
        </ModalHeader>
        <RetailToolsRetailVdpModals
          testId={testId}
          customizationMetadata={customizationData}
          dealerData={currentCustomizationData}
          handleBlur={handleBlur}
          handleInputFieldChange={handleInputFieldChange}
          emailInputError={emailInputError}
          phoneInputError={phoneInputError}
        />
      </RetailToolsEditModal>
    </React.Fragment>
  );
};

export default RetailToolsRetailVdp;
